import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, Navigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import TopPanel from "./TopPanel";
import Footer from "./Footer";
import {
  getProducts,
  updateCampaignStatus,
} from "../src/Services/campaignService";
const API_BASES = (() => {
  const host = window.location.hostname;
  if (host === "crm.whytefarms.com") return ["https://app.whytefarms.com"];      // CRM domain -> app.whytefarms
  if (host === "3.22.224.17") return ["http://13.207.176.232:3000"];             // 3.22 IP -> 13.207 IP
  return ["https://app.whytefarms.com", "http://13.207.176.232:3000"];           // localhost / baaki: pehle domain, phir IP
})();
const DETAILS_PATH = "/api/campaigns/details";
const USAGES_PATH = "/api/campaigns/customer-usages";

const fetchWithFallback = async (path, options) => {
  let lastErr;
  for (const base of API_BASES) {
    try {
      const res = await fetch(`${base}${path}`, options);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res;
    } catch (e) {
      lastErr = e;
      console.warn(`[fetch] ${base}${path} failed:`, e.message);
    }
  }
  throw lastErr;
};

// Same as above, but returns parsed JSON and also moves on to the next server
// when the response is not an HTTP error but still unusable (success:false, empty list, bad JSON...).
// `isValid(json)` decides if a response is good enough; if no server gives a valid one,
// the last parsed response is returned so the caller can still show "empty".
const fetchJsonWithFallback = async (path, isValid = () => true) => {
  let lastErr;
  let lastJson = null;
  for (const base of API_BASES) {
    try {
      const res = await fetch(`${base}${path}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json?.success === false) throw new Error(json.message || "success:false");
      lastJson = json;
      if (!isValid(json)) throw new Error("empty / unusable response");
      return json;
    } catch (e) {
      lastErr = e;
      console.warn(`[fetch] ${base}${path} failed:`, e.message);
    }
  }
  if (lastJson) return lastJson;
  throw lastErr;
};

// accepts [..], { data: [..] }, { data: { usages: [..] } }, { usages: [..] } ...
const toList = (json) => {
  if (Array.isArray(json)) return json;
  const d = json?.data ?? json?.usages ?? json?.customers ?? json?.result ?? json?.list ?? json?.items;
  if (Array.isArray(d)) return d;
  const inner = d?.usages ?? d?.customers ?? d?.list ?? d?.items ?? d?.rows ?? json?.usages ?? json?.customers;
  return Array.isArray(inner) ? inner : [];
};
const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== "");
const fmtDateTime = (v) => {
  const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
  return d && !isNaN(d) ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
};
// one table row per customer usage
const normalizeUsage = (u) => {
  const cust = u.customer || {};
  const amount = pick(u.discountSoFar, u.discount, u.discountGiven, u.benefitGiven, u.benefitAmount, u.benefit, u.amount, u.discountAmount, u.stats?.benefitGiven);
  const applied = pick(u.appliedVia, u.applied_via, u.appliedBy, u.applyMode, u.source, u.channel);
  const when = pick(u.startDate, u.usedAt, u.appliedAt, u.date, u.createdAt, u.availedAt, u.timestamp);
  return {
    name: String(pick(u.customerName, u.name, u.fullName, cust.name, cust.fullName, u.userName, "—")),
    // API gives customerId (no phone), shown under the name
    phone: String(pick(u.customerId, u.phone, u.mobile, u.phoneNumber, u.mobileNumber, u.customerPhone, cust.phone, cust.mobile, "")),
    // product id (if the API ever sends it) -> name is looked up in products_data via resolveSub()
    productId: String(pick(u.productId, u.product_id, u.product?.id, u.product?.productId, u.subscription?.productId, "")),
    // "Unknown Product" from the API is treated as missing so resolveSub() can fill the real name
    sub: (() => {
      const raw = String(pick(u.productName, u.subscriptionId, u.subscription, u.subscriptionCode, u.subscriptionName, u.subId, u.orderId, ""));
      return /^unknown( product)?$/i.test(raw.trim()) ? "" : raw;
    })(),
    appliedVia: applied ? cap(String(applied)) : "—",
    date: fmtDateTime(when),
    rawDate: when,
    discount: amount !== undefined && !isNaN(Number(amount)) ? `₹${Number(amount).toLocaleString("en-IN")}` : "—",
    status: String(cap(pick(u.status, u.usageStatus, u.state, "Applied"))),
  };
};

// Name shown in the SUBSCRIPTION column:
// 1) name sent by the API, 2) row's productId -> products_data, 3) the campaign's eligible product(s) -> products_data
const resolveSub = (row, products, campaign) => {
  if (row.sub) return row.sub;
  const nameOf = (pid) => products.find((p) => p.id === pid || p.productId === pid)?.name;
  if (row.productId) {
    const n = nameOf(row.productId);
    if (n) return n;
  }
  const ids = campaign?.eligibility?.productIds || [];
  const names = ids.map((pid) => nameOf(pid) || pid).filter(Boolean);
  return names.length ? names.join(", ") : "—";
};

const GREEN = "#3b5e40";
const cardStyle = { borderRadius: "10px", border: "1px solid #e0e0e0" };
const TYPE_LABEL = {
  coupon: "Coupon / Discount",
  offer: "Offer",
  wallet_cashback: "Wallet Cashback",
  referral: "Refer & Earn",
};
const DOT = { Active: "#28a745", Scheduled: "#007bff", Expired: "#dc3545", Inactive: "#6c757d" };

// ---------- helpers ----------
const toJsDate = (ts) => (ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null);
const fmtDate = (ts) => {
  const d = toJsDate(ts);
  return d ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
};
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "—");
const displayStatus = (c) => {
  const now = new Date();
  const start = toJsDate(c.startAt);
  const end = toJsDate(c.endAt);
  if (end && end < now) return "Expired";
  if (c.status === "inactive") return "Inactive";
  if (start && start > now) return "Scheduled";
  return "Active";
};

const pill = (bg, color, extra = {}) => ({
  backgroundColor: bg, color, padding: "4px 10px", borderRadius: "15px",
  fontSize: "11px", fontWeight: "600", ...extra,
});

const renderStatusBadge = (status) => {
  const applied = status === "Applied";
  return (
    <span style={{ ...pill(applied ? "#e6f0e9" : "#f1f3f5", applied ? "#2c5a31" : "#495057"), fontSize: "13px", padding: "4px 12px" }}>
      {status}
    </span>
  );
};

const InfoRow = ({ label, value, last }) => (
  <div className={`d-flex justify-content-between py-2 ${last ? "" : "border-bottom"}`} style={{ fontSize: "13px" }}>
    <span className="text-muted" style={{ fontWeight: "700" }}>{label}</span>
    <span style={{ fontWeight: "600", fontSize: "14px", textAlign: "right" }}>{value}</span>
  </div>
);

const Kpi = ({ label, value, color }) => (
  <div className="col-12 col-md-4 mb-3 px-2">
    <div className="card shadow-sm h-100" style={cardStyle}>
      <div className="card-body py-3">
        <p className="text-muted mb-1" style={{ fontSize: "16px", fontWeight: "600" }}>{label}</p>
        <h4 style={{ fontWeight: "bold", margin: 0, fontFamily: "serif", fontSize: "24px", color }}>{value}</h4>
      </div>
    </div>
  </div>
);

function CouponsDiscountDetails() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [products, setProducts] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const [customersData, setCustomersData] = useState([]);

  const loadUsages = async () => {
    try {
      const json = await fetchJsonWithFallback(
        `${USAGES_PATH}?campaignId=${encodeURIComponent(id)}`,
        (j) => toList(j).length > 0
      );
      console.log("[customer-usages] raw response:", json);
      const list = toList(json);
      console.log("[customer-usages] first row keys:", list[0] ? Object.keys(list[0]) : "no rows found");
      setCustomersData(list.map(normalizeUsage));
    } catch (e) {
      console.error("Could not load customer usages: " + e.message);
      setCustomersData([]);
    }
  };

  const load = async () => {
    setLoading(true);
    loadUsages();
    try {
      const [json, prods] = await Promise.all([
        fetchJsonWithFallback(`${DETAILS_PATH}?campaignId=${encodeURIComponent(id)}`, (j) => !!j?.data),
        getProducts().catch(() => []),
      ]);
      setProducts(prods || []);
      if (json?.success === false || !json?.data) {
        setCampaign(null);
        setNotFound(true);
      } else {
        setCampaign(json.data);
        setNotFound(false);
      }
    } catch (e) {
      console.error("Could not load the campaign: " + e.message);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const toggleStatus = async () => {
    try {
      await updateCampaignStatus(campaign.id, campaign.status === "inactive" ? "active" : "inactive");
      load();
    } catch (e) { console.error(e); }
  };

  const exportCsv = () => {
    const head = ["Customer", "Phone", "Subscription", "Applied via", "Date", "Discount", "Status"];
    const body = customersData.map((r) => [r.name, r.phone, resolveSub(r, products, campaign), r.appliedVia, r.date, r.discount, r.status]);
    const csv = [head, ...body].map((r) => r.map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `${campaign?.campaignCode || id}-customers.csv`;
    a.click();
  };

  const wrap = (content) => (
    <>
      {loading && (
        <div className="loader-overlay">
          <div className="spinner-border" role="status" style={{ width: "3.5rem", height: "3.5rem", color: GREEN }} />
        </div>
      )}
      <div className="container-scroller" style={{ backgroundColor: "#f9f7f2" }}>
        <TopPanel />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel" style={{ backgroundColor: "#f9f7f2", minHeight: "100vh" }}>
            <div className="content-wrapper" style={{ padding: "2rem" }}>{content}</div>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );

  if (!campaign) {
    return wrap(
      <div>
        <Link to="/all-campaigns" style={{ color: GREEN, fontWeight: 600 }}>← All Campaigns</Link>
        {!loading && notFound && <h4 className="mt-4 text-muted">Campaign not found.</h4>}
      </div>
    );
  }

  // Refer & Earn has its own page, so this screen is not used for it
  if (campaign.type === "referral") {
    return <Navigate to="/refer-earn" replace />;
  }

  // ---------- derived data ----------
  const c = campaign;
  const cfg = c.config || {};
  const elig = c.eligibility || {};
  const stats = c.stats || {};
  const status = displayStatus(c);
  const prodName = (pid) => products.find((p) => p.id === pid || p.productId === pid)?.name || pid;
  const eligibleProducts = (elig.productIds || []).map(prodName).join(", ") || "All products";
  const benefitGiven = stats.benefitGiven ?? ((stats.discountGiven ?? 0) + (stats.creditGiven ?? 0));

  const couponCode = cfg.code || c.code;
  const applyLabel = { code: "Code", auto: "Auto-apply", both: "Code + Auto-apply" }[cfg.applyMode];
  const appliedBy = c.type === "coupon"
    ? [applyLabel && couponCode ? `${applyLabel} ${couponCode}` : applyLabel || couponCode, cfg.allowAgentApply ? "support" : null].filter(Boolean).join(" · ") || "—"
    : "—";

  let benefit = "—";
  if (c.type === "coupon") {
    const dType = cfg.discountType ?? c.discountType;
    const dValue = cfg.discountValue ?? c.discountValue;
    benefit = dType === "percent" || dType === "percentage" ? `${dValue}% off each delivery` : `₹${dValue} off`;
  } else if (c.type === "offer") {
    const b = cfg.benefit || {};
    benefit = b.kind === "free_product"
      ? [b.qty, b.unit, "free", prodName(b.productId)].filter(Boolean).join(" ")
      : `₹${b.amount} special price`;
  }

  const createdBy = [c.createdByName || c.createdBy, c.createdAt ? fmtDate(c.createdAt) : null].filter(Boolean).join(" · ") || "—";

  const filteredCustomers = customersData.filter((r) => {
    const q = search.trim().toLowerCase();
    const matchesSearch = !q || (r.name || "").toLowerCase().includes(q) || (r.phone || "").includes(q);

    return matchesSearch;
  });

  return wrap(
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-2">
        <div>
          <div className="d-flex align-items-center gap-2 mb-2">
            <span style={pill("#e6f0e9", "#2c5a31")}>{TYPE_LABEL[c.type] || c.type}</span>
            <span style={pill("#f1f3f5", "#495057", { display: "inline-flex", alignItems: "center", gap: 5 })}>
              <span style={{ width: 6, height: 6, backgroundColor: DOT[status], borderRadius: "50%", display: "inline-block" }} /> {status}
            </span>
            <span style={{ color: "#6c757d", fontSize: "12px", fontFamily: "monospace" }}>{c.campaignCode || c.id}</span>
          </div>
          <div className="d-flex align-items-center flex-wrap gap-3">
            <h2 style={{ fontWeight: "700", fontFamily: "serif", color: "#2c2c2c", margin: 0, fontSize: "33px" }}>{c.name}</h2>
          </div>
          {c.description && <p className="text-muted mt-2 mb-0">{c.description}</p>}
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-light bg-white border fw-bold text-dark" style={{ borderRadius: 8, fontSize: 15 }} onClick={exportCsv}>Export CSV</button>
          <button className="btn btn-light bg-white border fw-bold text-dark" style={{ borderRadius: 8, fontSize: 15 }} onClick={() => navigate(`/edit-campaign/${c.id}`)}>Edit</button>
          {status !== "Expired" && (
            <button
              className={`btn btn-light bg-white fw-bold ${c.status === "inactive" ? "text-success" : "text-danger"}`}
              style={{ borderRadius: 8, fontSize: 15, border: `1px solid ${c.status === "inactive" ? "#28a745" : "#dc3545"}` }}
              onClick={toggleStatus}
            >
              {c.status === "inactive" ? "Activate" : "Deactivate"}
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="row mb-3 mx-0">
        <Kpi label="Customers used" value={stats.customersUsed ?? 0} />
        <Kpi label="Subscriptions generated" value={stats.subscriptionsGenerated ?? stats.totalRedemptions ?? stats.customersUsed ?? 0} />
        <Kpi label="Total benefit given" value={inr(benefitGiven)} color="#a56627" />
      </div>

      {/* Chart & Info */}
      <div className="row mb-4">
        <div className="col-12 mb-3">
          <div className="card shadow-sm h-100" style={cardStyle}>
            <div className="card-body">
              <h6 style={{ fontWeight: "bold", marginBottom: 20, fontSize: 22 }}>Campaign information</h6>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", columnGap: 48 }}>
              <InfoRow label="Dates" value={`${fmtDate(c.startAt)} – ${fmtDate(c.endAt)}`} />
              {c.type === "coupon" && <InfoRow label="Applied by" value={appliedBy} />}
              <InfoRow label="Eligible product" value={eligibleProducts} />
              {c.type === "offer" && cfg.condition && (
                <InfoRow label="Required qty" value={[cfg.condition.value, cfg.condition.unit].filter(Boolean).join(" ")} />
              )}
              <InfoRow label="Min duration" value={elig.minDurationDays ? `${elig.minDurationDays} days` : "—"} />
              <InfoRow label="Customer type" value={cap(elig.customerType)} />
              <InfoRow label="Acquisition source" value={(elig.acquisitionSources || []).join(", ") || "Any"} />
              <InfoRow label="Benefit" value={benefit} />
              <InfoRow label="Per customer" value={c.maxUsesPerCustomer ? `${c.maxUsesPerCustomer} time` : "Unlimited"} />
              <InfoRow label="Created by" value={createdBy} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="card shadow-sm" style={cardStyle}>
        <div className="card-body p-0">
          <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
            <h6 style={{ fontWeight: "bold", margin: 0, fontSize: 19 }}>Customers who availed it</h6>
            <input type="text" className="form-control form-control-sm w-25" placeholder="Customer or mobile"
              style={{ borderRadius: 6 }} value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0" style={{ fontSize: 12 }}>
              <thead style={{ color: "#6c757d", fontSize: 10, letterSpacing: "0.5px" }}>
                <tr>
                  <th className="pt-3 pb-3 ps-4 border-0">CUSTOMER</th>
                  <th className="pt-3 pb-3 border-0">SUBSCRIPTION</th>
                  <th className="pt-3 pb-3 border-0">APPLIED VIA</th>
                  <th className="pt-3 pb-3 border-0">DATE</th>
                  <th className="pt-3 pb-3 border-0 text-end">DISCOUNT SO FAR</th>
                  <th className="pt-3 pb-3 border-0 ps-4">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted py-5">No customers have used this campaign yet.</td></tr>
                )}
                {filteredCustomers.map((row, idx) => (
                  <tr key={idx} style={{ verticalAlign: "middle" }}>
                    <td className="ps-4 py-3">
                      <div style={{ fontWeight: "bold", color: "#2c2c2c", fontSize: 16 }}>{row.name}</div>
                      <div style={{ fontSize: 12, color: "#6c757d", marginTop: 6 }}>{row.phone}</div>
                    </td>
                    <td className="py-3 text-muted" style={{ fontWeight: "700" }}>{resolveSub(row, products, campaign)}</td>
                    <td className="py-3 text-muted" style={{ fontWeight: "700" }}>{row.appliedVia}</td>
                    <td className="py-3 text-muted" style={{ fontWeight: "700" }}>{row.date}</td>
                    <td className="py-3 text-end" style={{ color: "#a56627", fontWeight: "700" }}>{row.discount}</td>
                    <td className="py-3 ps-4">{renderStatusBadge(row.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default CouponsDiscountDetails;